import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, take, tap, throwError } from 'rxjs';

import { LoaderService } from '@shared/services/loader.service';
import { NotificationService } from '@shared/services/notification.service';
import { environment } from 'src/environments/environment';

type ServiceOptions = {
  useLoader?: boolean;
  useErrorNotification?: boolean;
  httpOptions?: HttpOptions;
  baseUrl?: 'baseApiUrl' | 'jobsApiUrl';
};

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);
  private loaderService = inject(LoaderService);

  buildHttpParams(obj: { [key: string]: any }): HttpParams {
    let params = new HttpParams();
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== null && value !== undefined) {
        params = params.set(key, value);
      }
    });
    return params;
  }

  get<T>(
    partialurl: string,
    { httpOptions = {}, useLoader = true, useErrorNotification = true, baseUrl = 'baseApiUrl' }: ServiceOptions = {},
  ): Observable<T> {
    if (useLoader) {
      this.loaderService.show();
    }
    return this.http.get<T>(this.generateUrl(baseUrl, partialurl), httpOptions).pipe(
      tap(() => useLoader && this.loaderService.hide()),
      catchError(err => {
        if (useLoader) {
          this.loaderService.hide();
        }
        if (useErrorNotification) {
          this.notificationService.error(err);
        }
        return throwError(() => err);
      }),
      take(1),
    );
  }

  getText(partialurl: string, { httpOptions = {}, baseUrl = 'baseApiUrl' }: ServiceOptions = {}) {
    return this.http.get(this.generateUrl(baseUrl, partialurl), { ...httpOptions, responseType: 'text' }).pipe(
      catchError(err => {
        this.notificationService.error(err);
        return throwError(() => err);
      }),
      take(1),
    );
  }

  post<T>(
    partialurl: string,
    body: any,
    { httpOptions = {}, useLoader = true, useErrorNotification = true, baseUrl = 'baseApiUrl' }: ServiceOptions = {},
  ) {
    if (useLoader) {
      this.loaderService.show();
    }
    return this.http.post<T>(this.generateUrl(baseUrl, partialurl), body, httpOptions).pipe(
      tap(() => useLoader && this.loaderService.hide()),
      catchError(err => {
        if (useLoader) {
          this.loaderService.hide();
        }
        if (useErrorNotification) {
          this.notificationService.error(err);
        }
        return throwError(() => err);
      }),
      take(1),
    );
  }

  /** POST for endpoints that answer with a bare string, which is not valid JSON. */
  postText(
    partialurl: string,
    body: unknown,
    { httpOptions = {}, useLoader = true, useErrorNotification = true, baseUrl = 'baseApiUrl' }: ServiceOptions = {},
  ) {
    const hideLoader = () => {
      if (useLoader) {
        this.loaderService.hide();
      }
    };

    if (useLoader) {
      this.loaderService.show();
    }
    return this.http.post(this.generateUrl(baseUrl, partialurl), body, { ...httpOptions, responseType: 'text' }).pipe(
      tap(hideLoader),
      catchError(err => {
        hideLoader();
        if (useErrorNotification) {
          this.notificationService.error(err);
        }
        return throwError(() => err);
      }),
      take(1),
    );
  }

  put<T>(
    partialurl: string,
    body: any,
    { httpOptions = {}, useLoader = true, useErrorNotification = true, baseUrl = 'baseApiUrl' }: ServiceOptions = {},
  ) {
    if (useLoader) {
      this.loaderService.show();
    }
    return this.http.put<T>(this.generateUrl(baseUrl, partialurl), body, httpOptions).pipe(
      tap(() => useLoader && this.loaderService.hide()),
      catchError(err => {
        if (useLoader) {
          this.loaderService.hide();
        }
        if (useErrorNotification) {
          this.notificationService.error(err);
        }
        return throwError(() => err);
      }),
      take(1),
    );
  }

  delete<T>(
    partialurl: string,
    { httpOptions = {}, useLoader = true, useErrorNotification = true, baseUrl = 'baseApiUrl' }: ServiceOptions = {},
  ) {
    if (useLoader) {
      this.loaderService.show();
    }
    return this.http.delete<T>(this.generateUrl(baseUrl, partialurl), httpOptions).pipe(
      tap(() => useLoader && this.loaderService.hide()),
      catchError(err => {
        if (useLoader) {
          this.loaderService.hide();
        }
        if (useErrorNotification) {
          this.notificationService.error(err);
        }
        return throwError(() => err);
      }),
      take(1),
    );
  }

  private generateUrl(baseUrl: 'baseApiUrl' | 'jobsApiUrl', partialurl: string) {
    const sanitizedBaseUrl = (environment[baseUrl] ?? '').replace(/\/$/, ''); // Remove trailing slash
    const sanitizedPartialUrl = (partialurl ?? '').replace(/^\//, ''); // Remove leading slash
    return `${sanitizedBaseUrl}/${sanitizedPartialUrl}`;
  }
}

export interface HttpOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: 'body';
  params?:
    | HttpParams
    | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
  transferCache?:
    | {
        includeHeaders?: string[];
      }
    | boolean;
}
